import {
  IconChartDown,
  IconChartUp,
  PopoverNavigation,
  PopoverNavigationItem,
  PopoverNavigationLink,
  PopoverTrigger,
  cssColorValue,
  semanticColors,
  spacer20,
  spacer24,
  spacer8,
} from '@spotify-internal/encore-web';
import React, { useRef, KeyboardEvent } from 'react';
import styled from 'styled-components';

import { NavigationSubMenuData } from 'utils/vertical';

import { StyledTextLink } from './StyledTextLink';

type Props = {
  menuKey: string;
  open: boolean;
  onOpen: (key: string) => void;
  onClose: (key: string) => void;
} & NavigationSubMenuData;

const StyledPopoverNavigation = styled(PopoverNavigation)`
  [dir='rtl'] & * {
    direction: rtl;
    & > svg {
      transform: rotateY(180deg);
    }
  }

  & ul {
    display: flex;
    flex-direction: column;
  }
`;

const MenuLink = styled(PopoverNavigationLink)`
  line-height: ${spacer20};
`;

const MenuTitle = styled(StyledTextLink)`
  display: flex;
  gap: ${spacer8};
  line-height: ${spacer24};

  &:hover,
  &:hover:not(:disabled) {
    color: ${cssColorValue(semanticColors.textBrightAccent)};
  }
`;

const StyledIconChartUp = styled(IconChartUp)`
  transform: translateY(1px);
`;

// TODO: Chevron icon aria label
export const NavigationMenu = ({ menuKey, open, onClose, onOpen, label: menuLabel, links }: Props) => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleOnMouseEnter = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    onOpen(menuKey);
  };

  const handleOnMouseLeave = () => {
    timer.current = setTimeout(() => {
      onClose(menuKey);
    }, 500);
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ') {
      e.preventDefault();
      if (open) {
        onClose(menuKey);
      } else {
        onOpen(menuKey);
      }
    }
  };

  const handleOnClick = () => {
    if (!open) {
      onOpen(menuKey);
    }
  };

  return (
    <PopoverTrigger
      onShow={() => {}}
      onHide={() => {}}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
      placement={PopoverTrigger.bottom}
      overlay={
        open && (
          <StyledPopoverNavigation arrow={PopoverNavigation.top}>
            {links.map(({ href, label }) => (
              <PopoverNavigationItem key={label}>
                <MenuLink href={href}>{label}</MenuLink>
              </PopoverNavigationItem>
            ))}
          </StyledPopoverNavigation>
        )
      }
    >
      <MenuTitle component="button" onKeyDown={handleOnKeyDown} onClick={handleOnClick}>
        <span>{menuLabel}</span>
        {open ? <StyledIconChartUp iconSize={16} /> : <IconChartDown iconSize={16} />}
      </MenuTitle>
    </PopoverTrigger>
  );
};
